<template>
  <div>
    <!-- Page Header -->
    <PageHeader :title="$t('navigation.dashboard')" :subtitle="$t('overview.subtitle')" />

    <!-- Whose money is this? In a two-pot app that question is the first one
         the dashboard has to answer, and until now the only answer was a 24px
         mark in the sidebar. The household's own photo and members go at the
         top of the shared pot: it is the surface people actually look at, and
         a shared budget should feel like a home rather than a database row.
         Personal context gets nothing here — there is no photo of "mitt", and
         the page title already says whose it is. -->
    <button
      v-if="activeHousehold"
      type="button"
      class="household-banner animate-in"
      @click="$emit('navigate', 'household')"
    >
      <AppImage
        v-if="activeHousehold.imageId"
        class="household-banner-photo"
        :image-id="activeHousehold.imageId"
        :alt="activeHousehold.name"
        aspect="1 / 1"
      />
      <span v-else class="household-banner-photo household-banner-photo--empty" aria-hidden="true">🏡</span>

      <span class="household-banner-text">
        <span class="household-banner-name">{{ activeHousehold.name }}</span>
        <span class="household-banner-meta">
          {{ activeHousehold.members.length }}
          {{ activeHousehold.members.length === 1 ? $t('household.member') : $t('household.members_word') }}
        </span>
      </span>

      <span class="household-banner-members">
        <AppAvatar
          v-for="member in activeHousehold.members.slice(0, 4)"
          :key="member.userId"
          size="sm"
          :image-id="member.avatarImageId"
          :name="member.displayName || member.email || '?'"
          :alt="member.displayName || member.email || ''"
        />
      </span>
    </button>

    <!-- New-user welcome. Leads the page: for someone with no data every card
         below is zeros, so the one thing worth doing has to come first. -->
    <EmptyState
      v-if="isNewUser"
      class="mb-8 animate-in"
      :title="$t('overview.no_data.title')"
      :message="$t('overview.no_data.subtitle')"
    >
      <div class="flex flex-wrap gap-3 justify-center">
        <AppButton style="margin: 0;" @click="goAddAccount">
          {{ $t('overview.no_data.add_account') }}
        </AppButton>
        <AppButton variant="outline" style="margin: 0;" @click="showImportModal = true">
          {{ $t('overview.no_data.import_data') }}
        </AppButton>
      </div>
    </EmptyState>

    <!-- Safe to Spend + Payday/Runway — the forecasting pair leads: "how much
         can I spend right now?" is the question the dashboard exists to
         answer, so it comes before the month totals. -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
      <div class="card animate-in" style="margin-bottom: 0;">
        <StatTile
          size="lg"
          :loading="!isReady"
          :label="$t('overview.safe_to_spend.title')"
          :value="formatMoney(safeToSpend.amount)"
          :tone="safeToSpend.amount >= 0 ? 'success' : 'danger'"
        >
          <template v-if="isReady">
            <p v-if="safeToSpend.amount < 0" style="font-size: var(--text-xs); color: var(--ruby-600); margin-top: 4px;">
              {{ $t('overview.safe_to_spend.over_hint', { amount: formatMoney(Math.abs(safeToSpend.amount)) }) }}
            </p>
            <p v-else style="font-size: var(--text-xs); color: var(--text-tertiary); margin-top: 4px;">
              {{ $t('overview.safe_to_spend.hint') }}
            </p>
          </template>
        </StatTile>
      </div>

      <div class="card animate-in relative" style="margin-bottom: 0;">
        <AppBadge
          v-if="showRunwayBadge"
          class="absolute inline-flex items-center gap-1"
          style="top: var(--spacing-5); right: var(--spacing-5);"
          :variant="runwayLevel === 'danger' ? 'danger' : runwayLevel === 'warning' ? 'warning' : 'success'"
          role="status"
          :aria-label="`${runwayMessage} — ${cashRunwayDisplay} ${$t('income.days_plural')}`"
        >
          {{ runwayMessage }}
        </AppBadge>
        <StatTile v-if="!isReady || hasIncomeSources" size="lg" :loading="!isReady" :label="$t('income.next_payday')">
          <template #value>
            {{ payday.daysUntilNextPayday }}
            <span style="font-size: var(--text-base); font-family: var(--font-body); color: var(--text-tertiary); font-weight: 400;">
              {{ payday.daysUntilNextPayday === 1 ? $t('income.days_singular') : $t('income.days_plural') }}
            </span>
          </template>
          <p v-if="isReady" style="font-size: var(--text-xs); color: var(--text-tertiary); margin-top: 4px; font-feature-settings: 'tnum';">
            {{ $t('overview.runway_hint', { days: cashRunwayDisplay }) }}
          </p>
        </StatTile>
        <template v-else>
          <p class="label-caps" style="margin-bottom: 8px;">{{ $t('income.next_payday') }}</p>
          <p style="font-size: var(--text-sm); color: var(--text-secondary); margin-bottom: 10px;">{{ $t('overview.no_income_sources') }}</p>
          <AppButton variant="link" @click="$emit('navigate', 'budgets')">
            {{ $t('overview.add_first_income') }} &rarr;
          </AppButton>
        </template>
      </div>
    </div>

    <!-- Month totals — supporting context under the forecasting pair. Two
         columns from the narrowest width and `fluid` sizing: one column of
         size="lg" tiles ran four full-height rows down a phone, pushing the
         fourth under the tab bar for a figure that is meant to be glanceable. -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" :aria-busy="!isReady">
      <StatTile
        class="animate-in"
        cell
        size="fluid"
        :loading="!isReady"
        :label="$t('overview.monthly_income')"
        :value="formatMoney(summary.totalIncome)"
        tone="success"
      />

      <StatTile
        class="animate-in"
        cell
        size="fluid"
        :loading="!isReady"
        :label="$t('overview.total_expenses')"
        :value="formatMoney(summary.totalExpenses)"
        tone="danger"
      />

      <StatTile
        class="animate-in"
        cell
        size="fluid"
        :loading="!isReady"
        :label="$t('overview.remaining_budget')"
        :value="formatMoney(summary.remaining)"
        :tone="summary.remaining >= 0 ? 'success' : 'danger'"
      />

      <StatTile
        class="animate-in"
        cell
        size="fluid"
        :loading="!isReady"
        :label="$t('overview.total_accounts')"
        :value="formatMoney(summary.accountsTotal)"
        tone="default"
      />
    </div>

    <!-- Weekly review. Above the briefing on purpose: the briefing tells you
         what happened, this is the one thing on the page you can finish. -->
    <div class="mb-8">
      <WeeklyReviewCard @open="showWeeklyReview = true" />
    </div>

    <!-- Proactive AI briefing (self-hides when unavailable) -->
    <div class="mb-8">
      <AiBriefingCard @navigate="$emit('navigate', $event)" />
    </div>

    <WeeklyReviewSheet v-model="showWeeklyReview" />

    <!-- Quick Add Section -->
    <div class="card animate-in">
      <!-- Title and toggle share a row only where both fit; on a phone the
           title was wrapping to two lines to make room for the segmented
           control beside it. -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h3 class="card-title" style="margin-bottom: 0;">{{ $t('overview.quick_add') }}</h3>
        <AppSegmented
          v-model="quickAddType"
          :options="[
            { value: 'expense', label: $t('expenses.transaction_types.expense'), tone: 'danger' },
            { value: 'income', label: $t('expenses.transaction_types.income'), tone: 'success' }
          ]"
          :aria-label="$t('overview.quick_add')"
        />
      </div>
      <div class="flex flex-col sm:flex-row gap-3">
        <div class="sm:w-32">
          <label class="sr-only" for="quick-add-amount">{{ $t('common.amount') }}</label>
          <AppInput
            id="quick-add-amount"
            ref="quickAddAmountInput"
            v-model="quickAddAmount"
            number
            type="number"
            inputmode="decimal"
            step="0.01"
            :placeholder="$t('common.amount')"
            :aria-invalid="quickAddAttempted && !quickAddAmount"
            @keyup.enter="handleQuickAdd"
          />
        </div>
        <div class="flex-1">
          <label class="sr-only" for="quick-add-description">{{ $t('common.description') }}</label>
          <AppInput
            id="quick-add-description"
            v-model="quickAddDescription"
            type="text"
            :placeholder="$t('common.description')"
            :aria-invalid="quickAddAttempted && !quickAddDescription"
            @change="applyQuickAddSuggestion"
            @keyup.enter="handleQuickAdd"
          />
        </div>
        <div class="w-full sm:w-44">
          <SearchableSelect
            v-model="quickAddCategory"
            :options="categories.map(c => ({ value: c.id, label: c.name, color: c.color }))"
            :placeholder="$t('common.category')"
            :search-placeholder="$t('common.search_placeholder')"
            :empty-text="$t('common.no_results')"
            :aria-label="$t('common.category')"
          />
        </div>
        <AppButton class="whitespace-nowrap" :loading="isQuickAdding" :disabled="isQuickAdding" @click="handleQuickAdd">
          {{ $t('common.add') }}
        </AppButton>
      </div>
      <p
        v-if="quickAddAttempted && (!quickAddAmount || !quickAddDescription || !quickAddCategory)"
        class="form-error"
      >
        {{ $t('common.amount_required') }}
      </p>

      <!-- Duplicate transaction warning -->
      <ConfirmDialog
        v-model="showQuickAddDuplicateConfirm"
        :title="$t('duplicate_warning.title')"
        :message="$t('duplicate_warning.message')"
        :confirm-text="$t('duplicate_warning.add_anyway')"
        :cancel-text="$t('duplicate_warning.cancel')"
        variant="warning"
        @confirm="confirmQuickAddDuplicate"
      />
    </div>

    <!-- Main Content Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
      <!-- Budget Alerts -->
      <div v-if="budgetAlerts.length > 0" class="card animate-in">
        <div class="mb-4" style="border-bottom: 1px solid var(--border-subtle); padding-bottom: 12px;">
          <p class="label-caps">{{ $t('overview.budget_alerts') }}</p>
        </div>
        <div class="space-y-4">
          <div
            v-for="alert in budgetAlerts"
            :key="alert.id"
          >
            <div class="flex items-center justify-between gap-2 mb-1">
              <span class="truncate min-w-0" style="font-weight: 500; font-size: var(--text-sm); color: var(--text-primary);">{{ alert.name }}</span>
              <!-- What's LEFT leads, not what's gone. A bare "84%" tells you
                   you've failed; "480 kr left this month" tells you what you
                   can still do, which is the only version anyone can act on. -->
              <span
                class="shrink-0"
                style="font-size: var(--text-sm); font-weight: 600; font-feature-settings: 'tnum';"
                :style="{ color: alert.remaining >= 0 ? 'var(--text-primary)' : 'var(--ruby-600)' }"
              >
                {{ alert.remaining >= 0
                  ? $t('overview.budget_left', { amount: formatMoney(alert.remaining) })
                  : $t('overview.budget_over', { amount: formatMoney(-alert.remaining) }) }}
              </span>
            </div>
            <AppProgress :value="alert.percentage" :color="alert.percentage >= 100 ? 'var(--ruby-500)' : 'var(--amber-500)'" />
            <p style="font-size: var(--text-xs); color: var(--text-tertiary); margin-top: 4px; font-feature-settings: 'tnum';">{{ $t('overview.budget_used', { percent: alert.percentage.toFixed(0) }) }} &middot; {{ formatMoney(alert.spent) }} / {{ formatMoney(alert.budget) }}</p>
          </div>
        </div>
      </div>

      <!-- Account Funding Needs -->
      <div v-if="accountFundingNeeds.length > 0" class="card animate-in">
        <div class="mb-4" style="border-bottom: 1px solid var(--border-subtle); padding-bottom: 12px;">
          <p class="label-caps">{{ $t('overview.account_funding') }}</p>
        </div>
        <div class="space-y-1">
          <div
            v-for="need in accountFundingNeeds"
            :key="need.accountId"
            class="flex items-center justify-between py-3"
            style="border-bottom: 1px solid var(--border-subtle);"
          >
            <div class="min-w-0 flex-1">
              <div class="flex items-center justify-between mb-1">
                <p style="font-weight: 500; font-size: var(--text-sm); color: var(--text-primary);" class="truncate">{{ need.accountName }}</p>
                <span
                  style="font-weight: 600; font-size: var(--text-sm); font-feature-settings: 'tnum';"
                  :style="{ color: need.needsTransfer ? 'var(--amber-600)' : 'var(--forest-600)' }"
                >
                  {{ need.needsTransfer ? $t('overview.amount_needed', { amount: formatMoney(need.shortfall) }) : $t('overview.funded') }}
                </span>
              </div>
              <AppProgress
                :value="(need.currentBalance / Math.max(need.monthlyExpenses, 1)) * 100"
                :color="need.needsTransfer ? 'var(--amber-500)' : 'var(--forest-500)'"
              />
              <div class="flex items-center justify-between" style="margin-top: 4px;">
                <span style="font-size: var(--text-xs); color: var(--text-tertiary); font-feature-settings: 'tnum';">{{ $t('overview.balance_label', { amount: formatMoney(need.currentBalance) }) }}</span>
                <span style="font-size: var(--text-xs); color: var(--text-tertiary); font-feature-settings: 'tnum';">{{ $t('overview.expenses_label', { amount: formatMoney(need.monthlyExpenses) }) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Upcoming Recurring Expenses -->
    <div v-if="upcomingExpenses.length > 0" class="card mt-5 animate-in">
      <div class="flex items-center justify-between mb-4" style="border-bottom: 1px solid var(--border-subtle); padding-bottom: 12px;">
        <p class="label-caps">{{ $t('overview.upcoming_expenses') }}</p>
        <AppButton variant="link" @click="$emit('navigate', 'transactions')">
          {{ $t('overview.view_all') }} &rarr;
        </AppButton>
      </div>

      <div>
        <div
          v-for="(item, index) in upcomingExpenses"
          :key="`${item.template.id}-${index}`"
          class="flex items-center justify-between py-3"
          style="border-bottom: 1px solid var(--border-subtle);"
        >
          <div class="min-w-0">
            <p style="font-weight: 500; font-size: var(--text-sm); color: var(--text-primary);" class="truncate">{{ item.template.description }}</p>
            <div class="flex items-center gap-2" style="margin-top: 2px;">
              <span style="font-size: var(--text-xs); color: var(--text-tertiary);">{{ formatUpcomingDate(item.dueDate) }}</span>
              <AppBadge variant="info">
                {{ frequencyLabelKey(item.template.frequency!) ? $t(frequencyLabelKey(item.template.frequency!)!) : item.template.frequency }}
              </AppBadge>
            </div>
          </div>
          <span
            class="shrink-0 ml-3"
            style="font-weight: 600; font-size: var(--text-sm); font-feature-settings: 'tnum';"
            :style="{ color: item.template.type === 'income' ? 'var(--forest-600)' : 'var(--ruby-600)' }"
          >
            {{ item.template.type === 'income' ? '+' : '-' }}{{ formatMoney(item.template.amount) }}
          </span>
        </div>
      </div>

      <div v-if="totalUpcomingAmount > 0" class="flex justify-between items-center" style="padding-top: 12px; margin-top: 4px;">
        <span style="font-size: var(--text-xs); color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.06em; font-weight: 500;">{{ $t('overview.total_upcoming_30_days') }}</span>
        <span style="font-weight: 600; color: var(--ruby-600); font-feature-settings: 'tnum';">-{{ formatMoney(totalUpcomingAmount) }}</span>
      </div>
    </div>

    <!-- Recent Transactions. Hidden for a brand-new user: with no accounts and
         no expenses its only content is a second empty state, stacked directly
         under the welcome one. -->
    <div v-if="!isNewUser" class="card mt-5 animate-in">
      <div class="flex items-center justify-between mb-4" style="border-bottom: 1px solid var(--border-subtle); padding-bottom: 12px;">
        <p class="label-caps">{{ $t('overview.recent_transactions') }}</p>
        <AppButton variant="link" @click="$emit('navigate', 'transactions')">
          {{ $t('overview.view_all') }} &rarr;
        </AppButton>
      </div>

      <div v-if="recentExpenses.length > 0">
        <div
          v-for="expense in recentExpenses"
          :key="expense.id"
          class="flex items-center justify-between py-3"
          style="border-bottom: 1px solid var(--border-subtle);"
        >
          <div class="flex items-center gap-3 min-w-0">
            <div
              class="shrink-0"
              style="width: 6px; height: 6px; border-radius: 1px;"
              :style="{ backgroundColor: getCategoryColor(expense.category) }"
            ></div>
            <div class="min-w-0">
              <p style="font-weight: 500; font-size: var(--text-sm); color: var(--text-primary);" class="truncate">{{ expense.description }}</p>
              <!-- Income rows have no category — show just the date instead of "None ·". -->
              <p style="font-size: var(--text-xs); color: var(--text-tertiary);" class="truncate">
                <template v-if="expense.type !== 'income' || expense.category">{{ getCategoryName(expense.category) }} &middot; </template>{{ formatDate(expense.date) }}
              </p>
            </div>
          </div>
          <span
            class="shrink-0 ml-3"
            style="font-weight: 600; font-size: var(--text-sm); font-feature-settings: 'tnum';"
            :style="{ color: expense.type === 'income' ? 'var(--forest-600)' : 'var(--ruby-600)' }"
          >
            {{ expense.type === 'income' ? '+' : '-' }}{{ formatMoney(expense.amount) }}
          </span>
        </div>
      </div>

      <EmptyState v-else :message="$t('overview.no_transactions')" />
    </div>

    <!-- Import Modal -->
    <AppModal v-model="showImportModal" :title="$t('overview.import.title')">
      <p style="font-size: var(--text-sm); color: var(--text-secondary); margin-bottom: 16px;">{{ $t('overview.import.description') }}</p>
      <AppTextarea
        v-model="importText"
        :placeholder="$t('overview.import.placeholder')"
        rows="8"
        style="background: var(--bg-secondary); font-family: 'SF Mono', 'Fira Code', monospace; resize: none;"
      />
      <div v-if="importError" style="font-size: var(--text-sm); color: var(--ruby-600); margin-top: 8px;">{{ importError }}</div>
      <div v-if="importSuccess" style="font-size: var(--text-sm); color: var(--forest-600); margin-top: 8px;">{{ importSuccess }}</div>
      <template #footer>
        <div class="flex justify-end gap-3">
          <AppButton variant="ghost" :disabled="isImporting" @click="showImportModal = false">
            {{ $t('common.cancel') }}
          </AppButton>
          <AppButton :disabled="!importText.trim()" :loading="isImporting" @click="handleImport">
            {{ isImporting ? $t('overview.import.importing') : $t('overview.import.import') }}
          </AppButton>
        </div>
      </template>
    </AppModal>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import AiBriefingCard from "../components/AiBriefingCard.vue"
import ConfirmDialog from "../components/ConfirmDialog.vue"
import WeeklyReviewCard from "../components/review/WeeklyReviewCard.vue"
import WeeklyReviewSheet from "../components/review/WeeklyReviewSheet.vue"
import {
  AppAvatar,
  AppBadge,
  AppButton,
  AppImage,
  // Must be a value import, not `type AppInput`: a type-only import compiles
  // and type-checks fine but erases at runtime, so <AppInput> silently failed
  // to resolve and the quick-add amount and description fields rendered as
  // nothing at all — leaving only the category dropdown and the Add button.
  // quickAddAmountInput is typed structurally (not InstanceType<typeof
  // AppInput>) so the binding never looks type-only — Biome's useImportType
  // pre-commit fix would rewrite it back to `type` and re-break quick-add.
  AppInput,
  AppModal,
  AppProgress,
  AppSegmented,
  AppTextarea,
  EmptyState,
  PageHeader,
  SearchableSelect,
  StatTile,
} from "../components/ui"
import { useCategorySuggestion } from "../composables/useCategorySuggestion"
import { formatMoney } from "../composables/useCurrency"
import { useFormatDate } from "../composables/useFormatDate"
import { useGlobalActions } from "../composables/useGlobalActions"
import { useToast } from "../composables/useToast"
import { getUpcomingRecurringExpenses } from "../services/recurringTransactionService"
import {
  addAccount,
  addCategory,
  addExpense,
  addIncome,
  budgetSummary,
  firestoreState,
  isFirestoreInitialized,
} from "../stores/firestoreStore"
import { currentHousehold } from "../stores/householdStore"
import { dispatchAction } from "../stores/uiStore"
import { computeAccountFundingNeeds } from "../utils/accountFunding"
import { countsTowardSpending, isPlanningTemplate } from "../utils/budgetVsActual"
import { frequencyLabelKey } from "../utils/frequencyLabel"
import { toLocalDateString } from "../utils/localDate"
import { calculateMonthlyIncome } from "../utils/monthlyIncome"
import { nextPaydayInfo } from "../utils/payday"

const { t } = useI18n()
const emit = defineEmits(["navigate"])
// null in personal context — the banner is household-only.
const activeHousehold = currentHousehold
const toast = useToast()
const { format: formatDateValue, formatRelative } = useFormatDate()

// Modal state
const showImportModal = ref(false)
const showWeeklyReview = ref(false)

// "Import data" from the command palette opens the JSON import modal here.
useGlobalActions({
  "import-data": () => {
    showImportModal.value = true
  },
  "weekly-review": () => {
    showWeeklyReview.value = true
  },
})
const importText = ref("")
const importError = ref("")

// Quick add state
const quickAddType = ref<"expense" | "income">("expense")
// Widened for AppInput's v-model (empty input emits ""); normalised with
// Number() on submit.
const quickAddAmount = ref<number | string | null>(null)
const quickAddDescription = ref("")
const quickAddCategory = ref("")
const quickAddAttempted = ref(false)
const quickAddAmountInput = ref<{ $el?: HTMLElement } | null>(null)

// Show skeletons for the headline numbers until the store's first load lands,
// so the dashboard doesn't flash zeros for returning users on a cold start.
const isReady = computed(() => isFirestoreInitialized.value)

// Data
const summary = computed(() => budgetSummary.value)
const accounts = computed(() => firestoreState.accounts.value)
const categories = computed(() => firestoreState.categories.value)
const expenses = computed(() => firestoreState.expenses.value)

// Whether any active recurring income exists — drives the payday card's
// empty state (income sources are managed under Budgets → Income).
const hasIncomeSources = computed(() => firestoreState.income.value.some((i) => i.isActive !== false))

// Next payday + cash runway, moved here from the removed Paychecks page.
// Runway = how long the money in accounts lasts at the average daily spend of
// the last 30 days (trailing window — "this month / days elapsed" is
// meaningless in the first days of a month).
const RUNWAY_CAP = 999
const payday = computed(() => nextPaydayInfo(firestoreState.income.value))

const cashRunwayDays = computed(() => {
  const totalBalance = accounts.value.reduce((sum, account) => sum + account.balance, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const cutoff = new Date(today)
  cutoff.setDate(cutoff.getDate() - 30)
  const cutoffStr = toLocalDateString(cutoff)
  const spentLast30Days = expenses.value
    .filter((e) => e.type === "expense" && countsTowardSpending(e) && e.date >= cutoffStr)
    .reduce((sum, e) => sum + e.amount, 0)
  const avgDailySpend = spentLast30Days / 30

  if (totalBalance <= 0) return 0
  if (avgDailySpend <= 0) return RUNWAY_CAP
  return Math.min(RUNWAY_CAP, Math.floor(totalBalance / avgDailySpend))
})

const cashRunwayDisplay = computed(() =>
  cashRunwayDays.value >= RUNWAY_CAP ? `${RUNWAY_CAP}+` : String(cashRunwayDays.value),
)

const runwayLevel = computed(() => {
  if (cashRunwayDays.value < 7) return "danger"
  if (cashRunwayDays.value < 14) return "warning"
  return "safe"
})

const runwayMessage = computed(() => {
  if (runwayLevel.value === "danger") return t("income.runway_critical")
  if (runwayLevel.value === "warning") return t("income.runway_low")
  return t("income.runway_safe")
})

// The runway badge is only meaningful once there is something to compute it
// from. Runway is balance ÷ trailing daily spend, and cashRunwayDays returns 0
// whenever the balance is 0 — so a user who simply hasn't added an account yet
// scored "danger" and was told KRITISK NIVÅ, in the same card that was already
// saying "no income sources yet" with an add-your-first link. The badge and the
// empty state contradicted each other, and "critical" was the first impression
// a brand-new user got. Also gated on isReady: without it the badge rendered on
// top of the loading skeleton, before any figure existed.
const showRunwayBadge = computed(() => isReady.value && hasIncomeSources.value && accounts.value.length > 0)

// Category lookup maps for O(1) access
const categoryMap = computed(() => {
  const map = new Map<string, { name: string; color: string }>()
  for (const cat of categories.value) {
    map.set(cat.id, { name: cat.name, color: cat.color })
    map.set(cat.name, { name: cat.name, color: cat.color })
  }
  return map
})

// "Safe to spend": this month's income minus what's been spent so far this
// month. (Days-until-payday lives on the payday card next to it.)
const safeToSpend = computed(() => {
  const activeIncomes = firestoreState.income.value.filter((i) => i.isActive !== false)
  const monthlyIncome = calculateMonthlyIncome(activeIncomes)

  const now = new Date()
  const month = now.getMonth() + 1
  const monthKey = `${now.getFullYear()}-${month < 10 ? `0${month}` : month}`
  // Templates and duplicate restatements are not money that left the account.
  // Counting templates here made "Trygt å bruke" disagree with the tiles right
  // below it by the entire recurring total — -44 107 kr against a remaining of
  // -13 295 kr, on the same screen.
  const spentThisMonth = expenses.value
    .filter((e) => e.type === "expense" && countsTowardSpending(e) && e.date.startsWith(monthKey))
    .reduce((sum, e) => sum + e.amount, 0)

  return { amount: monthlyIncome - spentThisMonth }
})

// Quick-add auto-categorization: when the user finishes typing a description
// and hasn't picked a category, pre-select the one used last time for the same
// description. Only fills an empty selection — never overrides a user choice.
const { suggestCategoryId } = useCategorySuggestion()
function applyQuickAddSuggestion() {
  if (quickAddCategory.value) return
  const suggested = suggestCategoryId(quickAddDescription.value)
  if (suggested) quickAddCategory.value = suggested
}

// A brand-new user: nothing worth showing anywhere on the dashboard. Gated on
const recentExpenses = computed(() => {
  // Templates are plans, not things that happened — the same rule totalExpenses
  // applies. Listing them here put each fixed cost next to the occurrence it
  // generated: two rows, same description, same date, same amount, reading as a
  // duplicated transaction. They belong in "Kommende" below, and are there.
  //
  // Copy before sorting — sort() mutates, and mutating a reactive array
  // inside a computed causes spurious re-renders and corrupts the source of
  // truth for every other consumer of state.expenses. Compare YYYY-MM-DD
  // strings directly since lex order is chronological.
  return [...expenses.value]
    .filter((e) => !isPlanningTemplate(e))
    .sort((a, b) => (b.date < a.date ? -1 : b.date > a.date ? 1 : 0))
    .slice(0, 5)
})

// A brand-new user: nothing worth showing anywhere on the dashboard. Gated on
// isReady so a returning user's cold start doesn't flash the welcome banner
// before the first load lands — the stat tiles guard the same way via :loading.
const isNewUser = computed(() => isReady.value && accounts.value.length === 0 && recentExpenses.value.length === 0)

// Upcoming recurring expenses for the next 30 days
const upcomingExpenses = computed(() => {
  return getUpcomingRecurringExpenses(30).slice(0, 5)
})

const totalUpcomingAmount = computed(() => {
  return getUpcomingRecurringExpenses(30)
    .filter((item) => item.template.type === "expense")
    .reduce((sum, item) => sum + item.template.amount, 0)
})

// Pre-aggregate this month's expense totals by category in one pass — without
// this, the budget-alerts computed below would re-filter all expenses once per
// category (O(N×M)). Scoped to the current calendar month because the alerts
// compare against MONTHLY budgets; all-time totals would flag every category
// as over budget after a few months of history.
const expenseTotalsByCategoryName = computed(() => {
  const now = new Date()
  const month = now.getMonth() + 1
  const monthKey = `${now.getFullYear()}-${month < 10 ? `0${month}` : month}`
  const totals = new Map<string, number>()
  for (const e of expenses.value) {
    if (e.type !== "expense") continue
    if (!e.date.startsWith(monthKey)) continue
    totals.set(e.category, (totals.get(e.category) || 0) + e.amount)
  }
  return totals
})

const budgetAlerts = computed(() => {
  const totals = expenseTotalsByCategoryName.value
  return categories.value
    .filter((c) => (c.budget || 0) > 0)
    .map((category) => {
      // Expenses reference categories by either id or name, so check both
      // (same pattern as categorySpentById in BudgetsView).
      const spent = totals.get(category.id) ?? totals.get(category.name) ?? 0
      const percentage = (spent / (category.budget || 1)) * 100
      return {
        id: category.id,
        name: category.name,
        budget: category.budget || 0,
        spent,
        percentage,
        // Negative when over. The card leads with this rather than the
        // percentage — see the comment on the template.
        remaining: (category.budget || 0) - spent,
      }
    })
    .filter((c) => c.percentage >= 80)
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 3)
})

// Account funding needs — "how much do I need to transfer into each account to
// cover its planned bills?" (the question you ask on payday). The real logic
// lives in a pure, unit-tested helper; see its doc comment for the model.
const accountFundingNeeds = computed(() => computeAccountFundingNeeds(expenses.value, accounts.value))

// Methods
function getCategoryName(categoryIdOrName: string) {
  return categoryMap.value.get(categoryIdOrName)?.name || categoryIdOrName || t("common.none")
}

function getCategoryColor(categoryIdOrName: string) {
  return categoryMap.value.get(categoryIdOrName)?.color || "#6b7280"
}

function formatDate(dateString: string) {
  return formatDateValue(dateString, "short")
}

function formatUpcomingDate(date: Date) {
  return formatRelative(date)
}

// Accounts are created in Settings now — jump there and open the add form.
function goAddAccount() {
  emit("navigate", "settings")
  dispatchAction("add-account")
}

// Both inputs submit on Enter and the button is tappable — without an
// in-flight guard a double Enter/tap creates duplicate rows (mirrors the
// isAddingExpense pattern in ExpensesView/BudgetsView).
const isQuickAdding = ref(false)

// Duplicate confirmation state (mirrors AddTransactionCard): addExpense
// rejects same-day same-amount entries, which is legitimate for quick-add —
// two 50 kr coffees in one day are two transactions, not a mistake.
const showQuickAddDuplicateConfirm = ref(false)
const pendingQuickAddDuplicate = ref<(() => Promise<void>) | null>(null)

async function handleQuickAdd() {
  quickAddAttempted.value = true
  if (isQuickAdding.value) return
  if (!quickAddAmount.value || !quickAddDescription.value || !quickAddCategory.value) return

  const category = categories.value.find((c) => c.id === quickAddCategory.value)
  const account = accounts.value.length > 0 ? accounts.value[0].name : ""
  const isIncome = quickAddType.value === "income"

  const performQuickAdd = async (skipDuplicateCheck = false) => {
    await addExpense(
      {
        type: quickAddType.value,
        amount: Number(quickAddAmount.value),
        description: quickAddDescription.value,
        category: category?.name || "",
        account: account,
        date: toLocalDateString(),
      },
      { skipDuplicateCheck },
    )

    toast.success(t(isIncome ? "toasts.income_added" : "toasts.expense_added"))

    quickAddAmount.value = null
    quickAddDescription.value = ""
    quickAddCategory.value = ""
    quickAddAttempted.value = false
    // Re-focus the amount for fast multi-entry on mobile. The ref is the
    // AppInput component; the native input sits inside its root div.
    setTimeout(() => {
      const root = quickAddAmountInput.value?.$el
      root?.querySelector("input")?.focus()
    }, 50)
  }

  isQuickAdding.value = true
  try {
    await performQuickAdd(false)
  } catch (error) {
    if (error instanceof Error && error.message === "DUPLICATE_TRANSACTION") {
      pendingQuickAddDuplicate.value = () => performQuickAdd(true)
      showQuickAddDuplicateConfirm.value = true
      return
    }
    toast.error(t("toasts.save_failed"))
  } finally {
    // Must always reset: without this the in-flight guard above latches on
    // and every later quick-add returns early, leaving the button stuck
    // disabled/loading until a reload.
    isQuickAdding.value = false
  }
}

async function confirmQuickAddDuplicate() {
  const fn = pendingQuickAddDuplicate.value
  pendingQuickAddDuplicate.value = null
  if (!fn) return
  isQuickAdding.value = true
  try {
    await fn()
  } catch {
    toast.error(t("toasts.save_failed"))
  } finally {
    isQuickAdding.value = false
  }
}

const isImporting = ref(false)
const importSuccess = ref("")

async function handleImport() {
  importError.value = ""
  importSuccess.value = ""
  isImporting.value = true

  try {
    const data = JSON.parse(importText.value)
    const importedCounts = { accounts: 0, categories: 0, expenses: 0, income: 0 }

    // Import accounts
    if (Array.isArray(data.accounts)) {
      for (const account of data.accounts) {
        if (account.name && typeof account.balance === "number") {
          await addAccount({
            name: account.name,
            balance: account.balance,
            type: account.type || "checking",
          })
          importedCounts.accounts++
        }
      }
    }

    // Import categories
    if (Array.isArray(data.categories)) {
      for (const category of data.categories) {
        if (category.name) {
          // Check if category already exists
          const exists = categories.value.some((c) => c.name.toLowerCase() === category.name.toLowerCase())
          if (!exists) {
            await addCategory({
              name: category.name,
              color: category.color || "#6b7280",
              budget: category.budget || 0,
            })
            importedCounts.categories++
          }
        }
      }
    }

    // Import expenses/transactions
    if (Array.isArray(data.expenses) || Array.isArray(data.transactions)) {
      const items = data.expenses || data.transactions
      for (const expense of items) {
        if (expense.description && typeof expense.amount === "number") {
          await addExpense({
            description: expense.description,
            amount: expense.amount,
            category: expense.category || "Other",
            account: expense.account || "",
            date: expense.date || toLocalDateString(),
            type: expense.type || "expense",
          })
          importedCounts.expenses++
        }
      }
    }

    // Import income sources
    if (Array.isArray(data.income)) {
      for (const income of data.income) {
        if ((income.source || income.name) && typeof income.amount === "number") {
          await addIncome({
            source: income.source || income.name,
            amount: income.amount,
            frequency: income.frequency || "monthly",
            date: income.date || toLocalDateString(),
            isActive: income.isActive !== false,
          })
          importedCounts.income++
        }
      }
    }

    const totalImported =
      importedCounts.accounts + importedCounts.categories + importedCounts.expenses + importedCounts.income

    if (totalImported > 0) {
      importSuccess.value = t("overview.import.success", importedCounts)
      setTimeout(() => {
        showImportModal.value = false
        importText.value = ""
        importSuccess.value = ""
      }, 2000)
    } else {
      importError.value = t("overview.import.no_valid_data")
    }
  } catch (error) {
    importError.value = t("overview.import.error")
  } finally {
    isImporting.value = false
  }
}
</script>
